import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, AlertTriangle, RotateCw, CalendarClock } from "lucide-react";

/**
 * Mandatory $100 biweekly-Friday retirement contribution banner.
 * Renders only when applicable (user >= 25, paid, pre-retirement).
 */
function fmtDue(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

export default function RetirementMonthlyBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  const rc = user?.retirement_contribution;
  if (!rc || !rc.applicable) return null;

  const amount = Number(rc.contribution_amount ?? rc.monthly_amount ?? 100);
  const paid = rc.paid_this_period ?? rc.paid_this_month;
  const period = rc.period ?? rc.month;

  const retry = async () => {
    setBusy(true);
    try {
      await api.post("/auth/retirement/retry");
      if (refresh) await refresh();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  if (paid) {
    return (
      <div className="mt-6 flex items-start gap-3 bg-forest/8 border border-forest/30 rounded-xl px-4 py-3 text-sm" data-testid="retirement-banner-paid">
        <ShieldCheck className="w-4 h-4 text-forest shrink-0 mt-0.5" strokeWidth={1.8} />
        <div className="text-ink">
          Retirement contribution of <strong>${amount.toFixed(2)}</strong> for the period beginning
          <strong> {fmtDue(period)}</strong> is paid.
          {rc.next_due && (
            <span className="text-moss"> Next Friday due: <strong>{fmtDue(rc.next_due)}</strong>.</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 flex items-start gap-3 bg-clay-light border border-clay/30 rounded-xl px-4 py-3 text-sm" data-testid="retirement-banner-due">
      <AlertTriangle className="w-4 h-4 text-clay shrink-0 mt-0.5" strokeWidth={1.8} />
      <div className="flex-1 text-ink">
        <div>
          <strong>Mandatory ${amount.toFixed(2)} retirement contribution</strong> is due
          {period ? <> for the biweekly Friday period beginning <strong>{fmtDue(period)}</strong></> : rc.next_due ? <> starting <strong>{fmtDue(rc.next_due)}</strong></> : null}.
        </div>
        <div className="flex items-center gap-1.5 text-xs text-moss mt-1">
          <CalendarClock className="w-3 h-3" strokeWidth={1.8} />
          Monthly on payday · pulled from your Spending account.
        </div>
        {rc.last_error && (
          <div className="text-xs text-clay mt-1">
            Last attempt failed: <span className="font-mono">{rc.last_error}</span>. Make sure your Spending account has at least ${amount.toFixed(2)} and tap retry.
          </div>
        )}
      </div>
      <button onClick={retry} disabled={busy}
        data-testid="retirement-retry-btn"
        className="inline-flex items-center gap-1.5 bg-forest text-white rounded-md px-3 py-1.5 text-xs font-medium hover:bg-forest-deep disabled:opacity-60 shrink-0">
        <RotateCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "Charging…" : "Retry now"}
      </button>
    </div>
  );
}
